/**
 * Fish names and field marks: English.
 *
 * ⚠️ ESTABLISHED COMMON NAMES, NOT TRANSLATIONS. An angler looking up a fish
 * types "wels catfish" or "asp", not "som" or "zherekh". Where a species has
 * no settled English name — the Amur and Caspian ones mostly — the accepted
 * book name is used, and it stays close to the Latin so the two can be matched.
 *
 * On why this file is separate and why there is no Latin here, see
 * species-north-ru.ts.
 */
export const РЫБЫ_EN: Record<string, string> = {
  // ── carps and minnows ───────────────────────────────────────────────────
  'fish.bream': 'common bream',
  'fish.bream_look':
    'Deep, flat as a palm, dark bronze. Moves in shoals over silt flats and the edges of holes.',
  'fish.silver_bream': 'silver bream',
  'fish.silver_bream_look':
    'Like a small bream, but the fins carry a red tinge and the scales are larger. Keeps to the bottom.',
  'fish.blue_bream': 'zope',
  'fish.blue_bream_look':
    'Narrow and deep, bluish back, very long anal fin. Travels in midwater rather than on the bottom.',
  'fish.white_bream': 'white-eye bream',
  'fish.white_bream_look':
    'Silvery, with a large milky eye and a blunt snout. Likes current over clean sand.',
  'fish.carp': 'carp',
  'fish.carp_look':
    'Thick, golden-brown, two pairs of barbels. Roots up the shallows and gives itself away by the clouds of silt.',
  'fish.wild_carp': 'wild carp',
  'fish.wild_carp_look':
    'The undomesticated carp: leaner, darker and far harder-fighting. Holds in timber and along reed walls.',
  'fish.crucian': 'crucian carp',
  'fish.crucian_look':
    'Round, golden, no barbels at all. Sits in weedy bays and silted ponds.',
  'fish.gibel_carp': 'gibel carp',
  'fish.gibel_carp_look':
    'Silvery, flatter than the crucian, with a serrated dorsal spine. Lives anywhere there is water.',
  'fish.roach': 'roach',
  'fish.roach_look': 'Silver, red eye, orange fins. Hangs in small shoals along the weed edge.',
  'fish.kutum': 'kutum',
  'fish.kutum_look':
    'A large dark roach of Caspian rivers, thick-lipped. Feeds on mussels over the shell bars.',
  'fish.rudd': 'rudd',
  'fish.rudd_look':
    'Golden, with scarlet fins and an upturned mouth. Feeds at the surface above weed beds.',
  'fish.ide': 'ide',
  'fish.ide_look':
    'Thick-set, pale bronze, with crimson fins. Holds on the seam between current and slack water.',
  'fish.chub': 'chub',
  'fish.chub_look':
    'Broad-headed, large dark scales, red tail. Lies under overhanging bushes.',
  'fish.dace': 'dace',
  'fish.dace_look': 'Small, silvery, slim-bodied, straight mouth. Sits on sandy runs.',
  'fish.tench': 'tench',
  'fish.tench_look':
    'Dark green and slimy, tiny barbels, red eye. Roots in the ooze along the reeds.',
  'fish.nase': 'nase',
  'fish.nase_look':
    'Dark back, hard crosswise underslung mouth, black gut lining. Scrapes algae off stones in fast water.',
  'fish.barbel': 'barbel',
  'fish.barbel_look':
    'Torpedo body, four barbels, underslung mouth. Lies on the bottom of fast stony runs.',
  'fish.dnieper_barbel': 'Dnieper barbel',
  'fish.dnieper_barbel_look':
    'The barbel of the Dnieper rapids, darker and shorter than the common one. Holds behind rocks in the current.',
  'fish.grass_carp': 'grass carp',
  'fish.grass_carp_look':
    'Long and cylindrical with a broad forehead. Grazes the weed beds, mowing down young reed.',
  'fish.black_carp': 'black carp',
  'fish.black_carp_look':
    'Nearly cast-iron dark, with heavy pharyngeal teeth. Crushes mussels on the bottom.',
  'fish.silver_carp': 'silver carp',
  'fish.silver_carp_look':
    'Silvery, broad forehead, eyes set low on the head. Cruises midwater and leaps clear at any noise.',
  'fish.bighead_carp': 'bighead carp',
  'fish.bighead_carp_look':
    'Bigger than the silver carp, blotched dark, with an enormous head. Holds in midwater over deep holes.',
  'fish.vimba': 'vimba bream',
  'fish.vimba_look':
    'Streamlined, with a humped snout and a dark back. Runs upstream in shoals.',
  'fish.sabrefish': 'sabrefish',
  'fish.sabrefish_look':
    'A flat sabre: straight back, curved belly, upturned mouth. Chases fry in midwater on the current.',
  'fish.shemaya': 'shemaya',
  'fish.shemaya_look':
    'A slim silver fish with a dark back. Feeds at the surface in running water.',
  'fish.bleak': 'bleak',
  'fish.bleak_look': 'Small, bright silver, upturned mouth. Dimples the surface in a busy shoal.',
  'fish.moderlieschen': 'sunbleak',
  'fish.moderlieschen_look':
    'Tiny and almost translucent. Lives right at the surface of still ponds.',
  'fish.gudgeon': 'gudgeon',
  'fish.gudgeon_look':
    'Mottled, two short barbels, flat belly. Rests on the sand at the tail of a run.',
  'fish.schneider': 'schneider',
  'fish.schneider_look':
    'Like a bleak, but with two dark stripes running along the flank. Keeps to fast water.',
  'fish.amur_bream': 'Amur bream',
  'fish.amur_bream_look':
    'The bream of the Amur: narrower than ours, straight-backed, fine-scaled. Shoals through the side channels.',
  'fish.volga_nase': 'Volga nase',
  'fish.volga_nase_look':
    'The nase of the Volga tributaries, paler than the Dnieper fish. Scrapes stones on the shallows.',
  'fish.bitterling': 'bitterling',
  'fish.bitterling_look':
    'Tiny and deep-bodied, with an iridescent stripe along the flank. Found only where freshwater mussels live.',
  'fish.amur_chebak': 'Amur chebak',
  'fish.amur_chebak_look':
    'The roach of the Amur: silver with a dark back. Shoals in the quiet side channels.',
  'fish.skygazer': 'predatory carp',
  'fish.skygazer_look':
    'A predator with a sharply upturned mouth and a high back. Hunts fry in the midwater of the Amur.',
  'fish.yellowcheek': 'yellowcheek carp',
  'fish.yellowcheek_look':
    'A large Amur predator with a yellow patch on the cheek. Drives shoals in fast water.',
  'fish.asp': 'asp',
  'fish.asp_look':
    'Streamlined and silver, with a jutting lower jaw. Smashes into fry at the surface.',

  // ── salmon and trout ────────────────────────────────────────────────────
  'fish.brown_trout': 'brown trout',
  'fish.brown_trout_look':
    'Spotted, with red dots in pale haloes. Holds behind stones in cold streams.',
  'fish.lake_trout': 'lake trout',
  'fish.lake_trout_look':
    'A large silvery trout of deep lakes, spots dark. Comes onto the shallows towards night.',
  'fish.rainbow_trout': 'rainbow trout',
  'fish.rainbow_trout_look':
    'Silver, with a pink band along the flank and black speckling. Feeds in midwater.',
  'fish.sea_trout': 'sea trout',
  'fish.sea_trout_look':
    'A migratory trout: silver from the sea, darkening to brown blotches in the river. Runs upstream to spawn.',
  'fish.salmon': 'Atlantic salmon',
  'fish.salmon_look':
    'Powerful and silver, with black crosses above the lateral line. Rests in holes along the spawning run.',
  'fish.arctic_char': 'Arctic char',
  'fish.arctic_char_look':
    'Dark back, pale unhaloed spots, belly from pink to deep red. Wants ice-cold water.',
  'fish.palia': 'palia char',
  'fish.palia_look':
    'The char of deep northern lakes: dark, with a white leading edge to the lower fins.',
  'fish.taimen': 'taimen',
  'fish.taimen_look':
    'A huge river salmon with a flat head and a red tail. The master of Siberian rivers.',
  'fish.danube_salmon': 'huchen',
  'fish.danube_salmon_look':
    'The taimen of the Danube: copper-brown, finely speckled, broad-tailed. Holds in deep pools.',
  'fish.marble_trout': 'marble trout',
  'fish.marble_trout_look':
    'A large trout of Balkan rivers: the flank is marbled, and there are no red spots at all.',
  'fish.lenok': 'lenok',
  'fish.lenok_look': 'Dark, finely speckled, blunt-snouted. Holds on the runs of Siberian rivers.',
  'fish.sevan_trout': 'Sevan trout',
  'fish.sevan_trout_look':
    'The trout of Lake Sevan: silvery, with sparse dark spots. Stays deep.',

  // ── whitefish ───────────────────────────────────────────────────────────
  'fish.whitefish': 'common whitefish',
  'fish.whitefish_look':
    'Silvery, underslung mouth, tight small scales. Shoals near the bottom of cold lakes.',
  'fish.vendace': 'vendace',
  'fish.vendace_look':
    'A small whitefish with an upturned mouth and a bluish back. Shoals in midwater.',
  'fish.peled': 'peled',
  'fish.peled_look':
    'A deep-bodied whitefish with a dark head and a terminal mouth. Picks plankton from midwater.',
  'fish.omul': 'omul',
  'fish.omul_look':
    'The whitefish of Baikal: slim and large-eyed. Follows the plankton in dense shoals.',
  'fish.broad_whitefish': 'broad whitefish',
  'fish.broad_whitefish_look':
    'A humped whitefish of northern rivers, thick-snouted, mouth underneath. Feeds on the bottom.',
  'fish.muksun': 'muksun',
  'fish.muksun_look':
    'The whitefish of Siberian rivers: silver, sharp-snouted. Keeps just off the bottom.',
  'fish.nelma': 'nelma',
  'fish.nelma_look':
    'A white salmon of whitefish stock: large, with a wide mouth. Hunts fry in the rivers of the north.',
  'fish.grayling': 'grayling',
  'fish.grayling_look':
    'Dark back and an enormous spotted sail of a dorsal fin. Holds behind stones in the current.',
  'fish.siberian_grayling': 'Siberian grayling',
  'fish.siberian_grayling_look':
    'Bigger than the European fish, the sail brighter and edged with red. Keeps to fast water.',

  // ── perches ─────────────────────────────────────────────────────────────
  'fish.perch': 'perch',
  'fish.perch_look':
    'Striped, with a spiny dorsal fin and red pelvic fins. Hunts in small packs.',
  'fish.zander': 'zander',
  'fish.zander_look':
    'Silver-grey, with fangs and a spiny crest. Works the bottom along drop-offs and holes.',
  'fish.volga_zander': 'Volga zander',
  'fish.volga_zander_look':
    'Like a zander but smaller, without fangs and with a blunter head. Sits on sandy holes.',
  'fish.ruffe': 'ruffe',
  'fish.ruffe_look':
    'Spiny, grey-brown with dark specks, covered in slime. Lies on the bottom in deep water.',
  'fish.donets_ruffe': 'Don ruffe',
  'fish.donets_ruffe_look':
    'A ruffe with a drawn-out snout and yellowish colouring. Keeps to sand in river currents.',
  'fish.zingel': 'zingel',
  'fish.zingel_look':
    'Long and cylindrical, banded dark, with two separate dorsal fins. Pressed flat to the bottom.',
  'fish.streber': 'streber',
  'fish.streber_look':
    'Slimmer and smaller than the zingel, the flank blotches more distinct. Lives on stony runs.',
  'fish.percarina': 'percarina',
  'fish.percarina_look':
    'A tiny, half-transparent perch of the estuaries, with large eyes. Drifts in midwater shoals.',

  // ── sturgeons ───────────────────────────────────────────────────────────
  'fish.sterlet': 'sterlet',
  'fish.sterlet_look':
    'A small sturgeon with a narrow pointed snout and fringed barbels. Works the bottom in current.',
  'fish.russian_sturgeon': 'Russian sturgeon',
  'fish.russian_sturgeon_look':
    'A blunt-snouted sturgeon with rows of bony scutes along the back. Feeds in deep holes.',
  'fish.siberian_sturgeon': 'Siberian sturgeon',
  'fish.siberian_sturgeon_look':
    'The sturgeon of Siberian rivers: darker than the Russian one, snout shorter. Stays on the bottom.',
  'fish.stellate_sturgeon': 'stellate sturgeon',
  'fish.stellate_sturgeon_look':
    'A sturgeon with a long sword-like snout and a slender body. Digs in deep sand.',
  'fish.beluga': 'beluga',
  'fish.beluga_look':
    'The largest freshwater fish of all: huge mouth, blunt snout. Lies in the deepest holes.',
  'fish.ship_sturgeon': 'ship sturgeon',
  'fish.ship_sturgeon_look':
    'A sturgeon with an unbroken lower lip — halfway in looks between the stellate and the Russian.',
  'fish.kaluga': 'kaluga',
  'fish.kaluga_look':
    'The beluga of the Amur: a giant with a broad mouth. Holds on the bottom of the big pools.',
  'fish.paddlefish': 'paddlefish',
  'fish.paddlefish_look':
    'A sturgeon relative with a long paddle instead of a snout. Strains plankton with its mouth open.',

  // ── catfishes ───────────────────────────────────────────────────────────
  'fish.catfish': 'wels catfish',
  'fish.catfish_look':
    'Bare dark body, wide mouth, two long barbels. Lies in holes and under fallen timber.',
  'fish.channel_catfish': 'channel catfish',
  'fish.channel_catfish_look':
    'Speckled, forked tail, four pairs of barbels. Works the bottom in loose groups.',
  'fish.amur_catfish': 'yellowhead catfish',
  'fish.amur_catfish_look':
    'A small catfish with yellow bands and sharp locking spines. Creaks audibly in the hand.',
  'fish.clarias': 'African sharptooth catfish',
  'fish.clarias_look':
    'Eel-like, with a very long dorsal fin, and it breathes air. Lives in warm coloured water.',

  // ── pikes ───────────────────────────────────────────────────────────────
  'fish.pike': 'pike',
  'fish.pike_look':
    'Long, dark green with yellow flecks, jaws full of teeth. Waits in ambush by weed and timber.',
  'fish.amur_pike': 'Amur pike',
  'fish.amur_pike_look':
    'The pike of the Amur: silvery, finely dark-speckled instead of yellow-flecked. Holds in side channels.',

  // ── sunfishes ───────────────────────────────────────────────────────────
  'fish.pumpkinseed': 'pumpkinseed',
  'fish.pumpkinseed_look':
    'Gaudy and round, with an orange spot on the gill cover. Fusses about in the weed.',
  'fish.largemouth_bass': 'largemouth bass',
  'fish.largemouth_bass_look':
    'Green, with a dark band along the flank and a mouth that reaches past the eye. Holds in timber.',
  'fish.smallmouth_bass': 'smallmouth bass',
  'fish.smallmouth_bass_look':
    'Bronze, with dark bars across the flank. Prefers stony bottom and moving water.',

  // ── gobies ──────────────────────────────────────────────────────────────
  'fish.round_goby': 'round goby',
  'fish.round_goby_look':
    'Dark, with a black spot on the dorsal fin; the pelvic fins are fused into a sucker. Sits on the bottom.',
  'fish.sand_goby': 'monkey goby',
  'fish.sand_goby_look':
    'Pale sand-coloured, flank finely spotted. Keeps to sandy shallows.',
  'fish.tubenose_goby': 'tubenose goby',
  'fish.tubenose_goby_look':
    'Tiny, with nostril tubes sticking out over the upper lip. Lives among stones.',

  // ── loaches ─────────────────────────────────────────────────────────────
  'fish.weatherfish': 'weatherfish',
  'fish.weatherfish_look':
    'Long and eel-like, dark-striped, with ten barbels. Gulps air to survive stagnant water.',
  'fish.spined_loach': 'spined loach',
  'fish.spined_loach_look':
    'Slim, a row of dark blotches, and a folding spine beneath the eye. Buries itself in sand.',
  'fish.stone_loach': 'stone loach',
  'fish.stone_loach_look': 'Smooth and mottled, six barbels. Hides under stones in clean streams.',

  // ── smelts ──────────────────────────────────────────────────────────────
  'fish.smelt': 'European smelt',
  'fish.smelt_look':
    'Translucent silver, small teeth, and a distinct smell of fresh cucumber. Travels in tight shoals.',
  'fish.snetok': 'snetok smelt',
  'fish.snetok_look':
    'A dwarf lake smelt, no longer than a finger. Shoals in the cold open water.',

  // ── lampreys ────────────────────────────────────────────────────────────
  'fish.river_lamprey': 'river lamprey',
  'fish.river_lamprey_look':
    'Not a fish at all: eel-shaped, jawless, with a round sucker where the mouth should be.',
  'fish.ukrainian_lamprey': 'Ukrainian brook lamprey',
  'fish.ukrainian_lamprey_look':
    'A small brook lamprey that never feeds as an adult. The larvae spend years buried in silt.',

  // ── others ──────────────────────────────────────────────────────────────
  'fish.rotan': 'Amur sleeper',
  'fish.rotan_look':
    'Dark and big-headed, with a large mouth. Takes over small ponds and eats everything in them.',
  'fish.snakehead': 'northern snakehead',
  'fish.snakehead_look':
    'A scaled snake-like head and a very long dorsal fin. Breathes air and guards its young.',
  'fish.burbot': 'burbot',
  'fish.burbot_look':
    'The only freshwater cod: slippery, mottled, with a single chin barbel. Comes alive in the cold.',
  'fish.eel': 'European eel',
  'fish.eel_look':
    'Snake-shaped and slippery, with tiny gill slits. Feeds the shallows by night, buried by day.',
  'fish.bullhead': 'bullhead',
  'fish.bullhead_look':
    'Big-headed, flattened, marbled, scaleless. Sits under stones in clean streams.',
  'fish.bigmouth_buffalo': 'bigmouth buffalo',
  'fish.bigmouth_buffalo_look':
    'Like a heavy-backed giant crucian with a forward-facing mouth. Feeds in midwater.',
  'fish.smallmouth_buffalo': 'smallmouth buffalo',
  'fish.smallmouth_buffalo_look':
    'Darker than the bigmouth, with the mouth underneath. Takes its food straight off the bottom.',
  'fish.tilapia': 'Nile tilapia',
  'fish.tilapia_look':
    'Deep-bodied, barred across the flank, with a long dorsal fin. Wants warm water.',
};
